import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User as UserIcon, ShieldCheck } from "lucide-react";
import { HeartIcon } from "../lib/icons";
import { IMAGES } from "../data/content";
import { useAuth } from "../lib/AuthContext";
import { useNavLinks } from "../lib/useNavLinks";
import { DonateComingSoonDialog } from "./DonateComingSoonDialog";

/* A single palette dash slides in under the hovered link and stays under
   the active page (audit follow-up): color rotates through the palette so
   each link owns one quiet color note, never a full row. */
const NAV_LINK_BASE =
  "relative pb-1 text-base font-medium transition-colors after:absolute after:left-0 after:-bottom-0.5 after:h-[3px] after:w-5 after:rounded-full after:transition-opacity after:duration-200 after:content-['']";
const DASH = ["after:bg-tangerine", "after:bg-cobalt", "after:bg-grass", "after:bg-blossom", "after:bg-yellow"];

export const Navbar = ({ onStart, onDonate, primaryLabel = "Start a campaign", secondaryLabel = "Donate" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [selfDonate, setSelfDonate] = useState(false);
  const handleDonate = onDonate || (() => setSelfDonate(true));
  const dark = true;
  const isActive = (href) => href.split("#")[0] !== "" && location.pathname === href.split("#")[0];
  const linkClass = (i, active) =>
    `${NAV_LINK_BASE} ${DASH[i % DASH.length]} ${active ? "text-ink after:opacity-100" : "text-olive hover:text-ink after:opacity-0 hover:after:opacity-100"}`; // top of page is now a light hero, keep nav text dark
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const navLinks = useNavLinks();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (hash) => (e) => {
    setOpen(false);
    // Cross-route hash: e.g., "/team#founder" or "/demo#campaigns"
    if (hash.startsWith("/")) {
      const [path, anchor] = hash.split("#");
      if (location.pathname !== path) {
        e.preventDefault();
        navigate(hash);
        setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" }), 250);
      } else {
        e.preventDefault();
        if (anchor) document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" });
        else window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    // In-page anchor — works on Home and /demo (both have the same section IDs).
    const landingPath = location.pathname === "/demo" || location.pathname.startsWith("/demo/")
      ? "/demo"
      : "/";
    if (location.pathname !== landingPath) {
      e.preventDefault();
      navigate(`${landingPath}${hash}`);
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 150);
    }
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-sand/80 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="h-1.5 w-full bg-yellow" data-testid="header-accent-bar" />
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Single unified row: logo + nav + actions */}
        <div className="flex h-28 items-center justify-between gap-6 lg:h-32">
          <Link to="/" data-testid="logo-link" className="flex items-center">
            <img src={IMAGES.logoWordmark} alt="My Rich Aunt" className="h-24 w-auto lg:h-28" />
          </Link>

          <div className="hidden items-center gap-5 whitespace-nowrap xl:flex">
            {navLinks.map((l, i) => (
              <a
                key={l.href}
                href={l.href}
                onClick={goToSection(l.href)}
                aria-current={isActive(l.href) ? "page" : undefined}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={linkClass(i, isActive(l.href))}
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/founding"
              data-testid="nav-founding"
              aria-current={isActive("/founding") ? "page" : undefined}
              className={linkClass(navLinks.length, isActive("/founding"))}
            >
              Founding donors
            </Link>
            <Link
              to="/contact"
              data-testid="nav-contact"
              aria-current={isActive("/contact") ? "page" : undefined}
              className={linkClass(navLinks.length + 1, isActive("/contact"))}
            >
              Contact
            </Link>
            {user ? (
              <Link
                to="/dashboard"
                data-testid="nav-dashboard"
                className={`inline-flex items-center gap-1.5 text-base font-medium transition-colors ${dark ? "text-olive hover:text-ink" : "text-sand/80 hover:text-sand"}`}
              >
                <UserIcon className="h-4 w-4" strokeWidth={1.75} />
                Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="nav-signin"
                className={`text-base font-medium transition-colors ${dark ? "text-olive hover:text-ink" : "text-sand/80 hover:text-sand"}`}
              >
                Sign in
              </Link>
            )}
            {user?.is_admin && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className={`inline-flex items-center gap-1.5 text-base font-semibold transition-colors ${dark ? "text-cobalt hover:text-ink" : "text-sand hover:text-sand/80"}`}
              >
                <ShieldCheck className="h-4 w-4" strokeWidth={2} />
                Admin
              </Link>
            )}
            <span className="mx-1 h-5 w-px bg-border" aria-hidden />
            <button
              data-testid="nav-start-campaign"
              onClick={onStart}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-base font-semibold transition-colors ${ secondaryLabel ? "border-2 border-ink bg-transparent text-ink hover:bg-ink hover:text-sand" : "bg-ink text-sand hover:bg-forest" }`}
            >
              {primaryLabel}
            </button>
            {secondaryLabel && (
              <button
                data-testid="nav-donate-button"
                onClick={handleDonate}
                className="group inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
              >
                <HeartIcon className="h-4 w-4 transition-transform group-hover:scale-110" />
                {secondaryLabel}
              </button>
            )}
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className={`xl:hidden transition-colors ${dark ? "text-ink" : "text-sand"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="xl:hidden overflow-hidden bg-sand/95 backdrop-blur-xl border-b border-border"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={goToSection(l.href)}
                  className="font-serif text-3xl text-ink"
                >
                  {l.label}
                </a>
              ))}
              <Link to="/founding" onClick={() => setOpen(false)} className="font-serif text-3xl text-ink">
                Founding donors
              </Link>
              <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-contact" className="font-serif text-3xl text-ink">
                Contact
              </Link>
              {user ? (
                <Link to="/dashboard" onClick={() => setOpen(false)} data-testid="mobile-dashboard" className="font-serif text-3xl text-ink">
                  Dashboard
                </Link>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-signin" className="font-serif text-3xl text-ink">
                  Sign in
                </Link>
              )}
              <button
                data-testid="mobile-start-campaign"
                onClick={() => { setOpen(false); onStart(); }}
                className={`mt-2 rounded-full px-5 py-3 text-base font-semibold ${secondaryLabel ? "border-2 border-ink bg-transparent text-ink" : "bg-ink text-sand"}`}
              >
                {primaryLabel}
              </button>
              {secondaryLabel && (
                <button
                  data-testid="mobile-donate-button"
                  onClick={() => { setOpen(false); handleDonate(); }}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-5 py-3 text-base font-semibold text-sand"
                >
                  <HeartIcon className="h-4 w-4" />
                  {secondaryLabel}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {!onDonate && secondaryLabel && <DonateComingSoonDialog open={selfDonate} onOpenChange={setSelfDonate} />}
    </motion.header>
  );
};
